import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/layout/AdminLayout";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { UserPlus, Shield, X } from "lucide-react";

const ROLE_BADGE = {
  super_admin: "bg-[#E63946] text-white",
  admin: "bg-[#0A192F] text-white",
  evaluator: "bg-amber-100 text-amber-800",
  scout: "bg-indigo-100 text-indigo-800",
  player: "bg-slate-200 text-slate-700",
  public: "bg-slate-100 text-slate-600",
};

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "evaluator", temporary_password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/users").then(({ data }) => setItems(data.items || []));
  useEffect(() => { load(); }, []);

  const invite = async () => {
    setError("");
    if (!form.name || !form.email || !form.temporary_password) {
      setError("Name, email and temporary password are required");
      return;
    }
    if (form.temporary_password.length < 8) {
      setError("Temporary password must be at least 8 characters");
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/users/invite", form);
      setShowForm(false);
      setForm({ name: "", email: "", role: "evaluator", temporary_password: "" });
      load();
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const setRole = async (userId, role) => {
    await api.patch(`/admin/users/${userId}/role`, { role });
    load();
  };

  const isSuperAdmin = me?.role === "super_admin";

  return (
    <AdminLayout>
      <div className="p-8 max-w-6xl">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="label-overline text-[#E63946]">Users</div>
            <h1 data-testid="admin-users-title" className="font-display text-4xl uppercase tracking-tighter mt-2">
              Staff & Accounts
            </h1>
            <p className="text-xs text-slate-500 mt-1">Manage admins, evaluators, scouts, players, and parent accounts.</p>
          </div>
          {isSuperAdmin && (
            <button
              data-testid="invite-user-button"
              onClick={() => { setShowForm(true); setError(""); }}
              className="inline-flex items-center gap-2 bg-[#0A192F] text-white px-5 py-3 text-sm font-semibold uppercase tracking-wider rounded-sm"
            >
              <UserPlus className="w-4 h-4" /> Invite Staff
            </button>
          )}
        </div>

        <div className="mt-8 bg-white border border-slate-200 rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left p-3 label-overline">Name</th>
                <th className="text-left p-3 label-overline">Email</th>
                <th className="text-left p-3 label-overline">Role</th>
                <th className="text-left p-3 label-overline">Created</th>
                <th className="text-right p-3 label-overline">Set Role</th>
              </tr>
            </thead>
            <tbody>
              {items.map((u) => (
                <tr key={u.id} className="border-b border-slate-100 last:border-0">
                  <td className="p-3 font-semibold">{u.name || "—"}</td>
                  <td className="p-3 text-xs text-slate-600">{u.email}</td>
                  <td className="p-3">
                    <span className={`px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded-sm ${ROLE_BADGE[u.role] || "bg-slate-100"}`}>
                      {u.role.replace("_", " ")}
                    </span>
                  </td>
                  <td className="p-3 text-xs text-slate-500">{new Date(u.created_at).toLocaleDateString("en-CA")}</td>
                  <td className="p-3 text-right">
                    {isSuperAdmin && u.role !== "super_admin" ? (
                      <select
                        data-testid={`set-role-${u.id}`}
                        value={u.role}
                        onChange={(e) => setRole(u.id, e.target.value)}
                        className="px-2 py-1 border border-slate-300 rounded-sm bg-white text-xs"
                      >
                        <option value="public">Public</option>
                        <option value="player">Player</option>
                        <option value="scout">Scout</option>
                        <option value="evaluator">Evaluator</option>
                        <option value="admin">Admin</option>
                      </select>
                    ) : (
                      <span className="text-xs text-slate-400">—</span>
                    )}
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan="5" className="p-8 text-center text-sm text-slate-500">No users.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4">
          <div className="bg-white max-w-md w-full rounded-sm">
            <div className="flex items-center justify-between p-5 border-b border-slate-200">
              <h2 className="font-display text-2xl uppercase tracking-tight">Invite Staff</h2>
              <button onClick={() => setShowForm(false)} className="p-2 hover:bg-slate-100"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-3">
              {error && <div className="border-l-4 border-[#E63946] bg-red-50 p-2 text-sm text-[#E63946]">{error}</div>}
              <div>
                <label className="label-overline">Full Name</label>
                <input data-testid="invite-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
              </div>
              <div>
                <label className="label-overline">Email</label>
                <input data-testid="invite-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
              </div>
              <div>
                <label className="label-overline">Role</label>
                <select data-testid="invite-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm bg-white text-sm">
                  <option value="evaluator">Evaluator</option>
                  <option value="scout">Scout</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div>
                <label className="label-overline">Temporary Password</label>
                <input data-testid="invite-password" value={form.temporary_password} onChange={(e) => setForm({ ...form, temporary_password: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
                <p className="text-[11px] text-slate-500 mt-1">Share this securely with the new user. They&apos;ll change it on first login (forthcoming).</p>
              </div>
            </div>
            <div className="p-5 border-t border-slate-200 flex justify-end gap-3">
              <button onClick={() => setShowForm(false)} className="px-5 py-2 border border-slate-300 text-sm font-semibold uppercase tracking-wider rounded-sm">Cancel</button>
              <button
                data-testid="invite-submit"
                onClick={invite}
                disabled={busy}
                className="px-5 py-2 bg-[#E63946] text-white text-sm font-semibold uppercase tracking-wider rounded-sm disabled:opacity-60"
              >
                {busy ? "Creating…" : "Create User"}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
